import React, { useContext, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';

import {
  Box,
  Dropdown,
  Flex,
  SiteVariablesPrepared,
} from '@fluentui/react-northstar';
import { Get } from '@microsoft/mgt-react';
import { separatorStyles } from '../../../../fluent-ui/components/Board/BoardLane';
import MgtCss from '../../../mgt.module.css';
import { getCachedFeature, hasConsentFeature } from '../../../shared/cache/FeatureCache';
import { ThemeColorScheme } from '../../../shared/common/TeamsTheme';
import { LICENSE_FEATURE } from '../../../shared/utils/constants';
import { i18n } from '../../../shared/utils/helper';
import ApprovalsTemplate from '../../WorkBoard/Templates/ApprovalTemplate/ApprovalsTemplate';
import { EmailTemplate } from '../../WorkBoard/Templates/EmailTemplate';
import { TicketTasksTemplate } from '../../WorkBoard/Templates/TicketTasksTemplate/TicketTasksTemplate';
import { TicketTemplate } from '../../WorkBoard/Templates/TicketTemplate';
import { IMyWorkBoardView } from './MyWorkBoard';
import { MyWorkContext } from '../MyWorkContextProvider';

const { t } = i18n();

interface Element {
  title: string;
  template: JSX.Element | JSX.Element[];
  hidden?: boolean;
}

const MY_WORK_TEMPLATES = {
  Tickets: (props: IMyWorkBoardView) => {
    return (<><TicketTemplate {...props } /></>)
  },
  Emails: () => {
    return (
      <Get
        scopes={['mail.read']}
        maxPages={2}
        version='beta'
        resource='/me/mailFolders/inbox/messages?$orderby=createdDateTime desc&$filter=isRead eq false and isDraft eq false'
      >
        <EmailTemplate template='value' />
      </Get>
    );
  },
  TicketTasks: () => <TicketTasksTemplate />,
  Approvals: () => <ApprovalsTemplate />
};

const dropdownDownItems = [
  t('my-work.lane-heading.my-tickets'),
  t('my-work.lane-heading.my-tasks'),
  t('my-work.lane-heading.my-approvals'),
];

// The lane's area under its heading. It does not scroll: each lane draws its cards in its own scroll area
// (VirtualLane), one page at a time and only those in view, as the ticket board does - so the card width cap and
// side padding moved there too (VirtualLane / LaneFrame).
const LaneArea: React.FC = ({ children }) => (
  <div className='my-work-lane-area' style={{ height: 'calc(100% - 51px)', width: '100%', color: 'var(--mgt-theme-default-foreground)' }}>
    {children}
  </div>
);

const MyWorkBoardView: React.FC<IMyWorkBoardView> = (props) => {
  const headerStyles = { fontWeight: 700, fontSize: 'inherit', paddingLeft: 5 };
  const [elements, setElements] = useState<Element[]>([
    {
      title: t('my-work.lane-heading.my-tickets'),
      template: <MY_WORK_TEMPLATES.Tickets {...props} />,
      hidden: false,
    },
    {
      title: t('my-work.lane-heading.my-tasks'),
      template: <MY_WORK_TEMPLATES.TicketTasks />,
      hidden: false,
    },
    {
      title: t('my-work.lane-heading.my-approvals'),
      template: <MY_WORK_TEMPLATES.Approvals />,
      hidden: false,
    },
  ]);
  const laneColSpan = Math.max(1, Math.floor(12 / elements.length));
  const [menu, setMenu] = useState(0);
  const { myWorkStates } = useContext(MyWorkContext);
  const isRefreshing = (myWorkStates.boardLanesRefreshing ?? 0) > 0;

  useEffect(() => {
    (async () => {
      // MY_WORK's ConsentFeatures entry was retired in #32824 along with the Agenda/Tasks-by-Planner
      // panes it authorized (Calendars.ReadWrite/Group.ReadWrite.All/Mail.Read) - calling it here
      // throws, since ConsentFeatures['MY_WORK'] no longer exists.
      await Promise.all([
        hasConsentFeature('EMAIL_CONNECTOR'),
        getCachedFeature(LICENSE_FEATURE.MultiDepartment),
        getCachedFeature(LICENSE_FEATURE.Collaborators)
      ]);

      setElements([
        {
          title: t('my-work.lane-heading.my-tickets'),
          template: <MY_WORK_TEMPLATES.Tickets {...props} />,
          hidden: false,
        },
        {
          title: t('my-work.lane-heading.my-tasks'),
          template: <MY_WORK_TEMPLATES.TicketTasks />,
          hidden: false,
        },
        {
          title: t('my-work.lane-heading.my-approvals'),
          template: <MY_WORK_TEMPLATES.Approvals />,
          hidden: false,
        },
      ]);
    })();
  }, []);

  return (
    <Container
      fluid={true}
      className={`h-full overflow-y-hidden my-work${isRefreshing ? ' board-refreshing' : ''}`}
    >
      <div className={`md:hidden py-3 my-work-lane-picker`}>
        <Flex hAlign="center">
          <Dropdown
            className='w-full'
            fluid
            items={dropdownDownItems}
            placeholder={t('my-work.lane-heading.my-tickets')}
            inverted
            onChange={(e, p) => {
              setMenu(p.highlightedIndex);
              // The lanes share the page's scroll area on a phone, so the next lane starts at its top.
              document.querySelector('.my-work-phone-body')?.scrollTo(0, 0);
            }}
          />
        </Flex>
      </div>
      <Row
        className={`${MgtCss.rowMyWork} my-work__board my-work__board--show-${menu}`}
        style={{flexWrap: 'nowrap'}}
      >
        {elements.map((element: Element, index: number) => (
          <Col
            className={`${MgtCss.colMyWork} my-work__board__lane`}
            key={`data_${index}`}
            xs={laneColSpan}
            sm={laneColSpan}
          >
            <div
              className={`hidden-on-mobile--important`}
              style={{
                marginLeft: 10,
                marginTop: 12,
                marginBottom: 15,
                ...headerStyles,
              }}
            >
              {element.title}
            </div>

            <Box
              // Height in main.css (my-work-lane-box): dvh, with vh as the fallback - a style object holds one value.
              className='my-work-lane-box'
              variables={({ colorScheme }: SiteVariablesPrepared) => ({
                backgroundColor: 'colorScheme.default.background2',
                separatorColor: colorScheme.default.border2,
              })}
              styles={{
                flex: '0 0 auto',
                padding: '0 0 .75rem 0',
                ...(index === elements.length - 1 ? {} : separatorStyles),
                ...ThemeColorScheme(props.siteVariables),
              }}
            >
              {!element.hidden && (
                <LaneArea>{element.template}</LaneArea>
              )}
            </Box>
          </Col>
        ))}
      </Row>
    </Container>
  );
};


export default MyWorkBoardView;
